import { useMemo } from 'react';
import { Link, useLocation, useMatches, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ShowSupportTicketResponse } from '@bliks-io/contracts';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@bliks-io/ui';
import { BetaBadge } from '@/components/beta-badge/BetaBadge';
import { useOrgSettings } from '@/api/queries/useOrgSettings';
import { supportTicketQueryOptions } from '@/api/queries/useSupportTicket';
import { useIsOrgManager } from '@/hooks/usePermissions';
import {
  useEntityBreadcrumbLabel,
  type BreadcrumbEntity,
  type EntityBreadcrumbLoaderData
} from '@/lib/entity-breadcrumb';
import { BETA_SEGMENTS } from '../nav/navigationConfig';

// Loaders contribute either the entity crumb's fields or a resolved label; a
// match that sets neither is just as valid, so every field is optional here.
type CrumbLoaderData =
  | (Partial<EntityBreadcrumbLoaderData> & {
      breadcrumb?: string;
      breadcrumbClearsSearch?: boolean;
    })
  | undefined;

interface CrumbItem {
  path: string;
  text: string;
  isLast: boolean;
  beta: boolean;
  linkable: boolean;
  /** Render as a link to its own pathname with search params cleared, even when last. */
  clearSearch?: boolean;
}

/**
 * Maps URL path segments (kebab-case) to breadcrumb translation keys.
 */
function getBreadcrumbTranslationKey(segment: string): string | null {
  const pathSegmentMap: Record<string, string> = {
    organization: 'organization',
    organigram: 'organigram',
    processes: 'processes',
    departments: 'departments',
    // Only the personal Meine Funktionen page lives on this segment — the org
    // catalog moved to the kind-fixed /organization/functions + …/teams pages.
    'org-functions': 'myOrgFunctions',
    functions: 'functions',
    teams: 'teams',
    systems: 'systems',
    'external-contacts': 'externalContacts',
    artifacts: 'artifacts',
    'org-members': 'orgMembers',
    'all-processes': 'allProcesses',
    'create-processes': 'createProcesses',
    'my-tasks': 'myTasks',
    'organization-settings': 'organizationSettings',
    'data-sync': 'dataSync',
    'initial-setup': 'initialSetup',
    'setup-pending': 'setupPending',
    'seat-overrun': 'seatOverrun',
    settings: 'organizationSettings',
    'process-map': 'processMap',
    'process-chains': 'processChains',
    'shadow-systems': 'shadowSystems',
    'bulk-upload': 'departmentsBulkUpload',
    'user-management': 'userManagement',
    apps: 'apps',
    editor: 'processEditor',
    'recording-dashboard': 'recordingDashboard',
    department: 'departments',
    members: 'orgMembers',
    landscape: 'systemLandscape',
    admin: 'admin',
    tenants: 'tenants',
    users: 'users',
    'partner-content': 'partnerContent',
    contacts: 'partnerContacts',
    'info-hub': 'partnerInfoHub',
    program: 'partnerProgram',
    calculator: 'partnerCalculator',
    partner: 'partner',
    leads: 'leads',
    'audit-logs': 'auditLogs',
    analytics: 'recordingAnalytics',
    'ai-usage': 'aiUsage',
    help: 'helpCenter',
    tickets: 'supportTickets'
  };

  return pathSegmentMap[segment] || null;
}

const LAYOUT_ONLY_SEGMENTS = new Set([
  'organization',
  'organization-settings',
  'initial-setup',
  'department',
  // /$slug/apps is a pass-through layout with no overview page (yet)
  'apps'
]);

// Catalog segments that live under /organization. Membership alone is not
// enough to classify a crumb — `teams` also exists at /department/teams and
// `departments` at /$slug/departments — so checks must also require the
// preceding segment to be `organization` (see isOrgChildSegment).
const ORG_CHILD_SEGMENTS = new Set([
  'departments',
  'org-members',
  'functions',
  'teams',
  'systems',
  'external-contacts',
  'artifacts'
]);

function isOrgChildSegment(segment: string, parentSegment: string | undefined): boolean {
  return ORG_CHILD_SEGMENTS.has(segment) && parentSegment === 'organization';
}

const PROCESS_EDITOR_ROUTE_ID = '/$slug/processes/editor/$processId';
const SUPPORT_TICKET_ROUTE_ID = '/$slug/help/tickets/$ticketId/';

const selectTicketNumber = (response: ShowSupportTicketResponse) => response.ticket.number;

/**
 * The ticket URL carries Zammad's internal id; the crumb names the ticket
 * number the customer knows, from the query the page itself runs.
 */
function useSupportTicketBreadcrumbLabel(ticketId: number | undefined) {
  const { t } = useTranslation();
  const { data: ticketNumber, isError } = useQuery({
    ...supportTicketQueryOptions(ticketId ?? 0),
    select: selectTicketNumber,
    enabled: ticketId !== undefined
  });

  if (ticketNumber !== undefined) return `#${ticketNumber}`;
  return isError ? t('supportTickets.detail.title') : t('common.loading');
}

const START_RECORDING_ROUTE_ID = '/$slug/processes/recording/start-recording/$processId';

const ENDS_IN_PARAM_SEGMENT = /\/\$[^/]*\/?$/;

// A pending start-recording match has no loaderData yet, but its crumb still
// names the process (resolved from the route param).
function hasEntityCrumb(match: { routeId: string; loaderData?: unknown }): boolean {
  return (
    Boolean((match.loaderData as CrumbLoaderData)?.breadcrumbEntity) ||
    match.routeId === START_RECORDING_ROUTE_ID
  );
}

// A pending param route has no loader label yet; without the placeholder its
// crumb would be missing until the loader resolves.
function loaderCrumbLabel(
  match: { routeId: string; status: string; loaderData?: unknown },
  loadingLabel: string
): string | undefined {
  const label = (match.loaderData as CrumbLoaderData)?.breadcrumb;
  if (label !== undefined) return label;
  return match.status === 'pending' && ENDS_IN_PARAM_SEGMENT.test(match.routeId)
    ? loadingLabel
    : undefined;
}

export function HeaderBreadcrumb() {
  const matches = useMatches();
  const { t } = useTranslation();
  const { data: orgSettings } = useOrgSettings();
  const isOrgManager = useIsOrgManager();
  const rootLabel = orgSettings?.name || t('breadcrumb.root');
  const hasSearchParams = useLocation({
    select: (location) => Object.keys(location.search).length > 0
  });

  // The entity detail routes deliberately do not resolve the name in their
  // loader (see entity-breadcrumb.ts); their crumbs read the query cache here.
  // The editor and start-recording routes show their pending UI before any
  // loader runs, so their crumb is keyed by the route param instead.
  // A layout and its child may both carry an entity reference, but they always
  // name the same entity, so the first one found serves the whole tree; each
  // match still applies its own suffix.
  const editorParams = useParams({ from: PROCESS_EDITOR_ROUTE_ID, shouldThrow: false });
  const startRecordingParams = useParams({ from: START_RECORDING_ROUTE_ID, shouldThrow: false });
  const paramProcessId = editorParams?.processId ?? startRecordingParams?.processId;
  const paramProcessEntity: BreadcrumbEntity | undefined = paramProcessId
    ? { kind: 'process', id: paramProcessId }
    : undefined;
  const entityBreadcrumbLabel = useEntityBreadcrumbLabel(
    matches.map((match) => (match.loaderData as CrumbLoaderData)?.breadcrumbEntity).find(Boolean) ??
      paramProcessEntity
  );

  const supportTicketId = useParams({
    from: SUPPORT_TICKET_ROUTE_ID,
    shouldThrow: false
  })?.ticketId;
  const supportTicketLabel = useSupportTicketBreadcrumbLabel(supportTicketId);
  const crumbLabel = (match: (typeof matches)[number]) => {
    if (hasEntityCrumb(match)) {
      return entityBreadcrumbLabel((match.loaderData as CrumbLoaderData)?.breadcrumbSuffix);
    }
    if (match.routeId === SUPPORT_TICKET_ROUTE_ID) return supportTicketLabel;
    return loaderCrumbLabel(match, t('common.loading'));
  };

  // Collect all matched pathnames to detect when a parent is already covered
  const matchedPaths = useMemo(
    () => new Set(matches.map((m) => m.pathname.replace(/\/$/, ''))),
    [matches]
  );

  // Get breadcrumb items from route matches
  const breadcrumbItems = matches
    .filter((match) => {
      if (match.pathname === '/') return false;
      // Skip the $slug layout match — org name is already the root breadcrumb.
      // The index route ('/$slug/') stays: it's the personal dashboard.
      const params = match.params as Record<string, string>;
      const segments = match.pathname.split('/').filter(Boolean);
      if (params.slug && segments.length === 1 && match.routeId !== '/$slug/') return false;
      return true;
    })
    .flatMap((match) => {
      // The personal dashboard lives on the $slug index route — there is no
      // path segment to derive a label from, so name it explicitly.
      if (match.routeId === '/$slug/') {
        return [
          {
            path: match.pathname,
            text: t('breadcrumb.dashboard'),
            isLast: true,
            beta: false,
            linkable: false
          }
        ];
      }
      const pathSegments = match.pathname.split('/').filter(Boolean);
      const lastSegment = pathSegments[pathSegments.length - 1];
      const loaderData = match.loaderData as CrumbLoaderData;
      const breadcrumb = crumbLabel(match);
      const beta = BETA_SEGMENTS.has(lastSegment);
      const isLast = match.pathname === matches[matches.length - 1]?.pathname;
      const isLayoutOnly = LAYOUT_ONLY_SEGMENTS.has(lastSegment);
      const isOrgChild = isOrgChildSegment(lastSegment, pathSegments[pathSegments.length - 2]);
      const linkable = !isLayoutOnly && (!isOrgChild || isOrgManager);

      const items: CrumbItem[] = [];

      // Editor route is not nested under all-processes in the route tree,
      // so synthesize the list + detail crumbs manually.
      if (match.routeId === PROCESS_EDITOR_ROUTE_ID) {
        const { slug, processId } = match.params;

        items.push({
          path: `/${slug}/processes/all-processes`,
          text: t('breadcrumb.allProcesses'),
          isLast: false,
          beta: false,
          linkable: true
        });
        items.push({
          path: `/${slug}/processes/all-processes/${processId}`,
          text: entityBreadcrumbLabel(),
          isLast: false,
          beta: false,
          linkable: true
        });
        items.push({
          path: match.pathname,
          text: t('breadcrumb.processEditor'),
          isLast: true,
          beta: false,
          linkable: false
        });
        return items;
      }

      // For detail pages with loader breadcrumb, synthesize a parent breadcrumb
      // if the parent segment (e.g. "departments") isn't already covered by another match
      if (breadcrumb && pathSegments.length >= 2) {
        const parentSegment = pathSegments[pathSegments.length - 2];
        const parentKey = getBreadcrumbTranslationKey(parentSegment);
        const parentIsOrgChild = isOrgChildSegment(
          parentSegment,
          pathSegments[pathSegments.length - 3]
        );
        const parentPath = parentIsOrgChild
          ? `/${pathSegments[0]}/organization/${parentSegment}`
          : `/${pathSegments.slice(0, -1).join('/')}`;
        if (parentKey && !matchedPaths.has(parentPath)) {
          items.push({
            path: parentPath,
            text: t(`breadcrumb.${parentKey}`),
            isLast: false,
            beta: BETA_SEGMENTS.has(parentSegment),
            linkable: !parentIsOrgChild || isOrgManager
          });
        }
      }

      if (breadcrumb) {
        items.push({
          path: match.pathname,
          text: breadcrumb,
          isLast,
          beta,
          linkable,
          // Routes that keep their internal navigation in search params declare
          // breadcrumbClearsSearch — their crumb then links back to the route's
          // start state whenever search params are present on the match.
          clearSearch: Boolean(loaderData?.breadcrumbClearsSearch) && hasSearchParams
        });
        return items;
      }

      // A segment without a key gets no crumb: the raw URL text would be
      // English in every locale, or an id.
      const translationKey = getBreadcrumbTranslationKey(lastSegment);
      if (translationKey) {
        items.push({
          path: match.pathname,
          text: t(`breadcrumb.${translationKey}`),
          isLast,
          beta,
          linkable
        });
      }
      return items;
    });

  // Deduplicate breadcrumb items by pathname (normalize by removing trailing slashes)
  const uniqueBreadcrumbItems = Array.from(
    breadcrumbItems
      .reduce((map, item) => {
        // Normalize path by removing trailing slash for deduplication
        const normalizedPath = item.path.replace(/\/$/, '');
        map.set(normalizedPath, item);
        return map;
      }, new Map<string, CrumbItem>())
      .values()
  )
    // Deduplicate consecutive items with identical display text
    .filter((item, i, arr) => i === 0 || item.text !== arr[i - 1].text);

  if (uniqueBreadcrumbItems.length === 0) {
    return null;
  }

  return (
    <Breadcrumb className="hidden ml-2 md:block text-[16px]">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbPage className="text-[16px]">{rootLabel}</BreadcrumbPage>
        </BreadcrumbItem>
        {uniqueBreadcrumbItems.map((item) => (
          <div key={item.path} className="flex items-center">
            <BreadcrumbSeparator className="px-2 text-[16px]">/</BreadcrumbSeparator>
            <BreadcrumbItem className="flex items-center gap-1.5">
              {(item.isLast && !item.clearSearch) || !item.linkable ? (
                <BreadcrumbPage className="text-[16px]">{item.text}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild className="text-[16px]">
                  <Link to={item.path} search={item.clearSearch ? {} : undefined}>
                    {item.text}
                  </Link>
                </BreadcrumbLink>
              )}
              {item.beta && <BetaBadge />}
            </BreadcrumbItem>
          </div>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
